import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';
import { User } from '../../types/admin';
import '../../components/Tables/Admin/AdminTable.css';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import { useToast } from '../../components/Common/Toast';
import { ConfirmModal } from '../../components/Common/ConfirmModal';
import { 
  Search, 
  Filter, 
  UserPlus, 
  Download, 
  RefreshCcw, 
  Calendar, 
  Shield, 
  Building2, 
  CheckCircle2, 
  XCircle,
  Clock,
  ArrowUpDown,
  MoreVertical,
  Mail,
  User as UserIcon,
  Crown,
  Phone
} from 'lucide-react';

/* Helper : détection des comptes suspects (heuristique UI) */
const DISPOSABLE_DOMAINS = ['mailnull.com','guerrillamail.com','tempmail.com','throwam.com','sharklasers.com','yopmail.com','trashmail.com','fakeinbox.com'];
const isSuspiciousAccount = (u: User): boolean => {
  const name = (u.full_name || '').trim();
  if (!name || name === u.email || name.length < 3) return true;
  if (/\d{4,}/.test(name)) return true; // nom contenant de nombreux chiffres
  const domain = u.email.split('@')[1] || '';
  if (DISPOSABLE_DOMAINS.includes(domain.toLowerCase())) return true;
  if (u.email.includes('+')) return true; // alias email (ex: test+1@gmail.com)
  return false;
};

/* ─────────────────────────────────────────────────────────────
   Helpers & Style Constants
   ───────────────────────────────────────────────────────────── */
const isSubActive = (end: string | null | undefined) => {
  if (!end) return false;
  return new Date(end) > new Date();
};

const fullPhone = (code: string | null | undefined, num: string | null | undefined) => {
  if (!num) return '—';
  return code ? `+${code.replace(/^\+/, '')} ${num}` : num;
};

const escCSV = (v: string | null | undefined) => {
  if (!v) return '';
  const s = String(v);
  if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const getWhatsAppUrl = (code: string | null | undefined, num: string | null | undefined) => {
  if (!num) return '';
  const cleanCode = code ? code.replace(/[^\d]/g, '') : '225';
  const cleanNum = num.replace(/[^\d]/g, '');
  if (cleanNum.startsWith(cleanCode)) {
    return `https://wa.me/${cleanNum}`;
  }
  return `https://wa.me/${cleanCode}${cleanNum}`;
};

const WhatsAppIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.003 5.324 5.328 0 11.896 0c3.181.001 6.171 1.242 8.42 3.496 2.248 2.253 3.487 5.244 3.484 8.425-.006 6.571-5.32 11.895-11.89 11.895-2.007-.001-3.98-.51-5.731-1.483L0 24zm6.59-2.046c1.657.982 3.275 1.503 4.887 1.505 5.568 0 10.096-4.524 10.1-10.092.002-2.698-1.047-5.234-2.955-7.144C16.962 4.312 14.43 3.262 11.73 3.26c-5.568 0-10.097 4.524-10.1 10.093-.001 1.91.504 3.778 1.464 5.438L2.094 22.06l3.963-.996c.162.088.326.173.49.255zM17.65 14.86c-.32-.16-1.89-.93-2.185-1.04-.3-.11-.515-.16-.73.16-.215.32-.83 1.04-1.02 1.255-.185.215-.37.24-.69.08-2.945-1.47-4.12-2.45-5.285-4.45-.315-.54.315-.5.9-.1.52.26 1.15.58 1.15.58s.22-.36.32-.51c.1-.15.2-.33.1-.5-.1-.17-.73-1.76-1-2.41-.265-.64-.53-.55-.73-.56-.185-.01-.4-.01-.615-.01-.215 0-.56.08-.855.4-.3.32-1.135 1.11-1.135 2.71 0 1.6 1.165 3.15 1.325 3.37.16.22 2.295 3.51 5.56 4.92.775.335 1.38.535 1.85.685.78.245 1.49.21 2.05.125.625-.09 1.89-.77 2.155-1.48.265-.71.265-1.32.185-1.44-.08-.12-.3-.2-.62-.36z" />
  </svg>
);

const inputSt: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  fontSize: '13px',
  backgroundColor: 'white',
  outline: 'none',
  minWidth: '150px',
  transition: 'border-color 0.2s',
};

const btnSt = (bg: string): React.CSSProperties => ({
  background: bg,
  color: '#fff',
  border: 'none',
  padding: '8px 16px',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  whiteSpace: 'nowrap',
});

const ROLE_COLORS: Record<string, { bg: string; color: string }> = {
  global_admin:    { bg: '#fef3c7', color: '#92400e' },
  admin_platform:  { bg: '#ede9fe', color: '#5b21b6' },
  paid_user_pro:   { bg: '#dcfce7', color: '#166534' },
  pro_user:        { bg: '#dcfce7', color: '#166534' },
  basic_user:      { bg: '#f1f5f9', color: '#475569' },
};

const getRoleStyle = (name?: string) => 
  ROLE_COLORS[name ?? ''] ?? { bg: '#f1f5f9', color: '#374151' };

/* ─────────────────────────────────────────────────────────────
   Edit User Modal
   ───────────────────────────────────────────────────────────── */
interface EditUserModalProps {
  user: User;
  roles: any[];
  tenants: any[];
  onClose: () => void;
  onSave: (userId: number, data: any) => Promise<void>;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ user, roles, tenants, onClose, onSave }) => {
  const [roleId, setRoleId] = useState<number>(user.role?.id || 0);
  const [tenantId, setTenantId] = useState<number>(user.tenant?.id || 0);
  const [isActive, setIsActive] = useState<boolean>(user.is_active);
  const [password, setPassword] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const availableRoles = roles.length > 0 ? roles : [
    { id: 1, name: 'global_admin' },
    { id: 2, name: 'admin_platform' },
    { id: 3, name: 'support_user' },
    { id: 4, name: 'basic_user' },
  ];

  const availableTenants = tenants.length > 0 ? tenants : [
    { id: 1, name: 'Default Tenant' },
    { id: 2, name: 'Premium Corp' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(user.id, {
        role_id: roleId,
        tenant_id: tenantId || null,
        is_active: isActive,
        password: password || undefined
      });
      onClose();
    } catch (error) {
      console.error("Failed to save user", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3 className="modal-title">Modifier l'utilisateur : {user.full_name || 'Sans nom'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Rôle</label>
            <select
              className="form-select"
              value={roleId}
              onChange={(e) => setRoleId(Number(e.target.value))}
            >
              {availableRoles.map(role => (
                <option key={role.id} value={role.id}>{role.name} ({role.description || 'Pas de description'})</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Tenant</label>
            <select
              className="form-select"
              value={tenantId}
              onChange={(e) => setTenantId(Number(e.target.value))}
            >
              <option value={0}>Aucun Tenant</option>
              {availableTenants.map(tenant => (
                <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              id="isActiveCheck"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              style={{ width: 'auto', margin: 0 }}
            />
            <label htmlFor="isActiveCheck" className="form-label" style={{ marginBottom: 0, cursor: 'pointer' }}>
              Compte Actif
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">Réinitialiser le mot de passe (Optionnel)</label>
            <input
              type="password"
              className="form-input"
              placeholder="Nouveau mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Enregistrement...' : 'Sauvegarder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   User Create Modal
   ───────────────────────────────────────────────────────────── */
interface UserCreateModalProps {
  roles: any[];
  tenants: any[];
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
}

const UserCreateModal: React.FC<UserCreateModalProps> = ({ roles, tenants, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role_id: roles.find(r => r.name === 'basic_user')?.id || roles[0]?.id || 0,
    tenant_id: 0,
    is_active: true
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error("Failed to create user", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3 className="modal-title">Nouveau Utilisateur</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Nom Complet</label>
            <input
              type="text"
              className="form-input"
              required
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Mot de Passe</label>
            <input
              type="password"
              className="form-input"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Rôle</label>
            <select
              className="form-select"
              value={formData.role_id}
              onChange={(e) => setFormData({ ...formData, role_id: Number(e.target.value) })}
            >
              {roles.map(role => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Tenant</label>
            <select
              className="form-select"
              value={formData.tenant_id}
              onChange={(e) => setFormData({ ...formData, tenant_id: Number(e.target.value) })}
            >
              <option value={0}>Aucun</option>
              {tenants.map(tenant => (
                <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
              ))}
            </select>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Création...' : 'Créer Utilisateur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Main Component: UserAdministration
   ───────────────────────────────────────────────────────────── */
interface UserFilters {
  search: string;
  status: string;
  role: string;
  tenant: string;
  plan: string;
  datePeriod: string;
  emailVerified: string;
  hasPhone: string;
  suspicious: string;
}

type SortField = 'id' | 'full_name' | 'email' | 'role' | 'is_active' | 'subscription_end_date' | 'last_login' | 'created_at';
type SortOrder = 'asc' | 'desc';

const UserAdministration: React.FC = () => {
  const { token } = useAuth();
  const { showToast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // État pour le modal de confirmation générique
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    isDestructive: boolean;
    isLoading: boolean;
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', isDestructive: true, isLoading: false, onConfirm: () => {} });

  const [revokeLoadingId, setRevokeLoadingId] = useState<number | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    status: '',
    role: '',
    tenant: '',
    plan: '',
    datePeriod: 'all',
    emailVerified: '',
    hasPhone: 'all',
    suspicious: '',
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const [sort, setSort] = useState<{ field: SortField; order: SortOrder }>({
    field: 'created_at',
    order: 'desc'
  });

  // Fetch Methods
  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/v1/admin/users?limit=1000`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [token]);

  const fetchRoles = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/v1/admin/roles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      }
    } catch (error) {
      console.error("Error fetching roles", error);
    }
  }, [token]);

  const fetchTenants = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/v1/admin/tenants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTenants(data);
      }
    } catch (error) {
      console.error("Error fetching tenants", error);
    }
  }, [token]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchUsers(), fetchRoles(), fetchTenants()]).finally(() => setLoading(false));
  }, [token, fetchUsers, fetchRoles, fetchTenants]);

  // Actions
  const handleSaveUser = async (userId: number, data: any) => {
    try {
      const response = await fetch(`${API_URL}/v1/admin/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(data)
      });
      if (response.ok) {
        const updatedUser = await response.json();
        setUsers(prev => prev.map(u => u.id === userId ? updatedUser : u));
        showToast('Utilisateur mis à jour avec succès', 'success');
      } else {
        const err = await response.json().catch(() => ({}));
        showToast(`Erreur : ${err.detail || 'Mise à jour échouée'}`, 'error');
      }
    } catch {
      showToast('Erreur de connexion', 'error');
    }
  };

  const handleCreateUser = async (data: any) => {
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...data, tenant_id: data.tenant_id || null })
      });
      if (response.ok) {
        setShowCreateModal(false);
        fetchUsers();
        showToast('Utilisateur créé avec succès', 'success');
      } else {
        const err = await response.json().catch(() => ({}));
        showToast(`Erreur : ${err.detail || 'Création échouée'}`, 'error');
      }
    } catch {
      showToast('Erreur de connexion', 'error');
    }
  };

  const handleToggleActive = (user: User) => {
    const action = user.is_active ? 'désactiver' : 'activer';
    setConfirmDialog({
      isOpen: true,
      title: user.is_active ? 'Désactiver le compte' : 'Activer le compte',
      message: `Voulez-vous vraiment ${action} le compte de ${user.full_name || user.email} ?`,
      isDestructive: user.is_active,
      isLoading: false,
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, isLoading: true }));
        try {
          const res = await fetch(`${API_URL}/v1/admin/users/${user.id}/toggle-status`, {
            method: 'PATCH',
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (res.ok) {
            const updated = await res.json();
            setUsers(prev => prev.map(u => u.id === user.id ? updated : u));
            showToast(`Compte ${updated.is_active ? 'activé' : 'désactivé'}`, updated.is_active ? 'success' : 'warning');
          } else {
            showToast('Erreur lors du changement de statut', 'error');
          }
        } catch {
          showToast('Erreur de connexion', 'error');
        } finally {
          setConfirmDialog(d => ({ ...d, isOpen: false, isLoading: false }));
        }
      },
    });
  };

  const handleRevokeSessions = (user: User) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Révoquer toutes les sessions',
      message: `Cette action déconnectera immédiatement ${user.full_name || user.email} de tous ses appareils. Continuer ?`,
      isDestructive: true,
      isLoading: false,
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, isLoading: true }));
        setRevokeLoadingId(user.id);
        try {
          const res = await fetch(`${API_URL}/v1/admin/users/${user.id}/revoke-sessions`, {
            method: 'PATCH',
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (res.ok) {
            showToast('Sessions révoquées — l\'utilisateur est déconnecté', 'success');
          } else {
            showToast('Erreur lors de la révocation', 'error');
          }
        } catch {
          showToast('Erreur de connexion', 'error');
        } finally {
          setRevokeLoadingId(null);
          setConfirmDialog(d => ({ ...d, isOpen: false, isLoading: false }));
        }
      },
    });
  };

  const handleResendVerification = (email: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Renvoyer l\'email de vérification',
      message: `Envoyer un nouvel email de vérification à ${email} ?`,
      isDestructive: false,
      isLoading: false,
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, isLoading: true }));
        try {
          const res = await fetch(`${API_URL}/auth/resend-verification`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          if (res.ok) {
            showToast('Email de vérification envoyé !', 'success');
          } else {
            showToast('Erreur lors de l\'envoi', 'error');
          }
        } catch {
          showToast('Erreur réseau', 'error');
        } finally {
          setConfirmDialog(d => ({ ...d, isOpen: false, isLoading: false }));
        }
      },
    });
  };

  // Sorting & Filtering
  const handleSort = (field: SortField) => {
    setSort(s => ({
      field,
      order: s.field === field && s.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  const renderSortArrow = (field: SortField) => {
    if (sort.field !== field) return null;
    return sort.order === 'asc' ? ' ↑' : ' ↓';
  };

  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Search
      if (filters.search) {
        const sLower = filters.search.toLowerCase();
        const matchesSearch =
          user.full_name?.toLowerCase().includes(sLower) ||
          user.email?.toLowerCase().includes(sLower) ||
          (user.phone_number || '').includes(sLower) ||
          String(user.id).includes(sLower);
        if (!matchesSearch) return false;
      }

      // Status
      if (filters.status) {
        if (filters.status === 'active' && !user.is_active) return false;
        if (filters.status === 'inactive' && user.is_active) return false;
        if (filters.status === 'sub_active' && !isSubActive(user.subscription_end_date)) return false;
        if (filters.status === 'sub_expired' && (!user.subscription_end_date || isSubActive(user.subscription_end_date))) return false;
      }

      // Role
      if (filters.role && user.role?.name !== filters.role) return false;

      // Tenant
      if (filters.tenant) {
        if (filters.tenant === 'none' && user.tenant?.name) return false;
        if (filters.tenant !== 'none' && user.tenant?.name !== filters.tenant) return false;
      }

      // Plan
      if (filters.plan && user.tenant?.plan !== filters.plan) return false;

      // Email verified
      if (filters.emailVerified) {
        if (filters.emailVerified === 'verified' && !user.is_email_verified) return false;
        if (filters.emailVerified === 'unverified' && user.is_email_verified) return false;
      }

      // Has Phone
      if (filters.hasPhone && filters.hasPhone !== 'all') {
        if (filters.hasPhone === 'yes' && !user.phone_number) return false;
        if (filters.hasPhone === 'no' && user.phone_number) return false;
      }

      // Date Period
      if (filters.datePeriod && filters.datePeriod !== 'all' && user.created_at) {
        const createdDate = new Date(user.created_at);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        if (filters.datePeriod === 'today' && createdDate < today) return false;
        
        if (filters.datePeriod === 'week') {
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          if (createdDate < weekAgo) return false;
        }
        
        if (filters.datePeriod === 'month') {
          const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
          if (createdDate < monthAgo) return false;
        }
      }

      // Suspicious filter
      if (filters.suspicious === 'yes' && !isSuspiciousAccount(user)) return false;
      if (filters.suspicious === 'no' && isSuspiciousAccount(user)) return false;

      return true;
    }).sort((a, b) => {
      const field = sort.field;
      const order = sort.order;
      let valA: any = a[field] || '';
      let valB: any = b[field] || '';

      if (field === 'role') {
        valA = a.role?.name || '';
        valB = b.role?.name || '';
      } else if (field === 'full_name' || field === 'email') {
        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();
      } else if (field === 'is_active') {
        valA = a.is_active ? 1 : 0;
        valB = b.is_active ? 1 : 0;
      }

      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [users, filters, sort]);

  // Statistics KPI
  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter(u => u.is_active).length;
    const premium = users.filter(u => {
      const name = u.role?.name;
      return name === 'paid_user_pro' || name === 'pro_user' || name === 'global_admin' || name === 'admin_platform';
    }).length;
    const subActive = users.filter(u => isSubActive(u.subscription_end_date)).length;
    const withPhone = users.filter(u => !!u.phone_number).length;
    return { total, active, premium, subActive, withPhone };
  }, [users]);

  const clearFilters = () => {
    setFilters({ 
      search: '', 
      status: '', 
      role: '', 
      tenant: '', 
      plan: '', 
      datePeriod: 'all', 
      emailVerified: '',
      hasPhone: 'all',
      suspicious: '',
    });
  };

  const exportToCSV = () => {
    const headers = ['ID', 'Nom', 'Email', 'Téléphone', 'Indicatif', 'Rôle', 'Actif', 'Email Vérifié', 'Abonnement fin', 'Dernière connexion', 'Inscription', 'Tenant', 'Plan'];
    const rows = filteredUsers.map(u => [
      String(u.id),
      escCSV(u.full_name),
      escCSV(u.email),
      escCSV(fullPhone(u.country_code, u.phone_number)),
      u.country_code ? `+${u.country_code.replace(/^\+/, '')}` : '',
      escCSV(u.role?.name),
      u.is_active ? 'Oui' : 'Non',
      u.is_email_verified ? 'Oui' : 'Non',
      u.subscription_end_date ? new Date(u.subscription_end_date).toLocaleDateString('fr-FR') : '—',
      u.last_login ? new Date(u.last_login).toLocaleString('fr-FR') : '—',
      u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '—',
      escCSV(u.tenant?.name),
      escCSV(u.tenant?.plan)
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `utilisateurs_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const activeFiltersCount = [
    filters.search,
    filters.status,
    filters.role,
    filters.tenant,
    filters.plan,
    filters.datePeriod !== 'all',
    filters.emailVerified,
    filters.hasPhone !== 'all',
    filters.suspicious,
  ].filter(Boolean).length;

  const totalPages = Math.ceil(filteredUsers.length / pageSize) || 1;

  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredUsers.slice(startIndex, startIndex + pageSize);
  }, [filteredUsers, currentPage, pageSize]);

  return (
    <SiteLayout>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .au-row {
          transition: background-color 0.15s ease;
        }
        .au-row:hover {
          background-color: rgba(99, 102, 241, 0.04) !important;
        }
        .au-btn {
          transition: all 0.2s ease;
        }
        .au-btn:hover {
          transform: translateY(-1px);
          opacity: 0.9;
        }
        .au-btn:active {
          transform: translateY(0);
        }
      `}</style>

      <Header icon='people' title='Administration des Utilisateurs' />

      <div style={{ padding: '20px', maxWidth: '1600px', margin: '0 auto', animation: 'fadeInUp 0.3s ease' }}>
        
        {/* Statistics KPI cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px', 
          marginBottom: '24px' 
        }}>
          {[
            { label: 'Total Utilisateurs', value: stats.total, icon: '👥', color: '#6366f1', bg: '#ede9fe' },
            { label: 'Utilisateurs Actifs', value: stats.active, icon: '✅', color: '#16a34a', bg: '#dcfce7' },
            { label: 'Premium / Admin', value: stats.premium, icon: '⭐', color: '#d97706', bg: '#fef3c7' },
            { label: 'Abonnés Actifs', value: stats.subActive, icon: '🔑', color: '#4f46e5', bg: '#e0e7ff' },
            { label: 'Avec Téléphone', value: stats.withPhone, icon: '📱', color: '#0284c7', bg: '#e0f2fe' },
          ].map(s => (
            <div key={s.label} style={{ 
              background: 'white', 
              border: '1px solid #e2e8f0', 
              borderRadius: '16px', 
              padding: '20px', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px', 
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)' 
            }}>
              <div style={{ 
                width: '48px', 
                height: '48px', 
                borderRadius: '12px', 
                background: s.bg, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '24px',
                flexShrink: 0
              }}>{s.icon}</div>
              <div>
                <div style={{ fontSize: '26px', fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', fontWeight: 500 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters Toolbar */}
        <div style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
            
            {/* Search Input */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 240px' }}>
              <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <Search size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Recherche
              </label>
              <input
                type="text"
                placeholder="Nom, email, tél, ID..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                style={{ ...inputSt, width: '100%' }}
              />
            </div>

            {/* Status Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <CheckCircle2 size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Statut Compte
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                style={inputSt}
              >
                <option value="">Tous les statuts</option>
                <option value="active">✅ Actif</option>
                <option value="inactive">❌ Inactif</option>
                <option value="sub_active">🔑 Abonnement Actif</option>
                <option value="sub_expired">⏰ Abonnement Expiré</option>
              </select>
            </div>

            {/* Role Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <Shield size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Rôle
              </label>
              <select
                value={filters.role}
                onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                style={inputSt}
              >
                <option value="">Tous les rôles</option>
                {roles.map(role => (
                  <option key={role.id} value={role.name}>{role.name}</option>
                ))}
              </select>
            </div>

            {/* Tenant Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <Building2 size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Tenant
              </label>
              <select
                value={filters.tenant}
                onChange={(e) => setFilters({ ...filters, tenant: e.target.value })}
                style={inputSt}
              >
                <option value="">Tous les tenants</option>
                <option value="none">Aucun tenant</option>
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.name}>{tenant.name}</option>
                ))}
              </select>
            </div>

            {/* Plan Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <Crown size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Plan
              </label>
              <select
                value={filters.plan}
                onChange={(e) => setFilters({ ...filters, plan: e.target.value })}
                style={inputSt}
              >
                <option value="">Tous les plans</option>
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>

            {/* Email Verified Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <Mail size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Email
              </label>
              <select
                value={filters.emailVerified}
                onChange={(e) => setFilters({ ...filters, emailVerified: e.target.value })}
                style={inputSt}
              >
                <option value="">Tous</option>
                <option value="verified">📧 Vérifié</option>
                <option value="unverified">❓ Non vérifié</option>
              </select>
            </div>

            {/* Phone presence filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <Phone size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Téléphone
              </label>
              <select
                value={filters.hasPhone}
                onChange={(e) => setFilters({ ...filters, hasPhone: e.target.value })}
                style={inputSt}
              >
                <option value="all">Tous</option>
                <option value="yes">📱 Avec numéro</option>
                <option value="no">🚫 Sans numéro</option>
              </select>
            </div>

            {/* Inscription Date Period Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <Calendar size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Inscription
              </label>
              <select
                value={filters.datePeriod}
                onChange={(e) => setFilters({ ...filters, datePeriod: e.target.value })}
                style={inputSt}
              >
                <option value="all">Toujours</option>
                <option value="today">Aujourd'hui</option>
                <option value="week">7 derniers jours</option>
                <option value="month">30 derniers jours</option>
              </select>
            </div>

            {/* Suspicious filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                ⚠️ Fiabilité
              </label>
              <select
                value={filters.suspicious}
                onChange={(e) => setFilters({ ...filters, suspicious: e.target.value })}
                style={inputSt}
              >
                <option value="">Tous</option>
                <option value="yes">⚠️ Suspects</option>
                <option value="no">✅ Fiables</option>
              </select>
            </div>


            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
              {activeFiltersCount > 0 && (
                <button
                  className="au-btn"
                  onClick={clearFilters}
                  style={{ ...btnSt('#ef4444'), fontWeight: 600 }}
                  title="Réinitialiser les filtres"
                >
                  <RefreshCcw size={14} /> Annuler ({activeFiltersCount})
                </button>
              )}

              <button
                className="au-btn"
                onClick={exportToCSV}
                style={btnSt('#10b981')}
                title="Exporter en CSV"
              >
                <Download size={14} /> Exporter
              </button>

              <button
                className="au-btn"
                onClick={() => setShowCreateModal(true)}
                style={btnSt('#6366f1')}
              >
                <UserPlus size={14} /> Ajouter
              </button>
            </div>

          </div>
        </div>

        {/* Counter bar */}
        <div style={{
          fontSize: '13px',
          color: '#64748b',
          marginBottom: '16px',
          display: 'flex',
          gap: '12px',
          alignItems: 'center'
        }}>
          <span>Affiché : <strong>{paginatedUsers.length}</strong> sur <strong>{filteredUsers.length}</strong> filtrés ({users.length} au total)</span>
          {activeFiltersCount > 0 && (
            <span style={{ 
              background: '#ede9fe', 
              color: '#7c3aed', 
              padding: '2px 10px', 
              borderRadius: '20px', 
              fontSize: '12px', 
              fontWeight: 600 
            }}>
              Filtres actifs
            </span>
          )}
        </div>

        {/* Users Table */}
        <Box>
          <div className='box-content-height-nobutton' style={{ overflowX: 'auto' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                <RefreshCcw size={24} className="animate-spin" style={{ margin: '0 auto 12px' }} />
                Chargement des utilisateurs en cours...
              </div>
            ) : (
              <table className='data-table' style={{ tableLayout: 'fixed', width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)' }}>
                    <th onClick={() => handleSort('id')} style={{ cursor: 'pointer', color: 'white', padding: '14px 16px', width: '5%', textAlign: 'left', userSelect: 'none' }}>
                      ID{renderSortArrow('id')}
                    </th>
                    <th onClick={() => handleSort('full_name')} style={{ cursor: 'pointer', color: 'white', padding: '14px 16px', width: '18%', textAlign: 'left', userSelect: 'none' }}>
                      Utilisateur{renderSortArrow('full_name')}
                    </th>
                    <th onClick={() => handleSort('email')} style={{ cursor: 'pointer', color: 'white', padding: '14px 16px', width: '18%', textAlign: 'left', userSelect: 'none' }}>
                      Email{renderSortArrow('email')}
                    </th>
                    <th style={{ color: 'white', padding: '14px 16px', width: '13%', textAlign: 'center' }}>
                      Téléphone
                    </th>
                    <th onClick={() => handleSort('role')} style={{ cursor: 'pointer', color: 'white', padding: '14px 16px', width: '9%', textAlign: 'center', userSelect: 'none' }}>
                      Rôle{renderSortArrow('role')}
                    </th>
                    <th onClick={() => handleSort('is_active')} style={{ cursor: 'pointer', color: 'white', padding: '14px 16px', width: '8%', textAlign: 'center', userSelect: 'none' }}>
                      Statut{renderSortArrow('is_active')}
                    </th>
                    <th onClick={() => handleSort('subscription_end_date')} style={{ cursor: 'pointer', color: 'white', padding: '14px 16px', width: '9%', textAlign: 'center', userSelect: 'none' }}>
                      Abonnement{renderSortArrow('subscription_end_date')}
                    </th>
                    <th onClick={() => handleSort('last_login')} style={{ cursor: 'pointer', color: 'white', padding: '14px 16px', width: '9%', textAlign: 'center', userSelect: 'none' }}>
                      Connexion{renderSortArrow('last_login')}
                    </th>
                    <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer', color: 'white', padding: '14px 16px', width: '8%', textAlign: 'center', userSelect: 'none' }}>
                      Inscription{renderSortArrow('created_at')}
                    </th>
                    <th style={{ color: 'white', padding: '14px 16px', width: '13%', textAlign: 'center' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={10} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                        👤 Aucun utilisateur ne correspond à vos filtres
                      </td>
                    </tr>
                  ) : paginatedUsers.map((user, index) => {
                    const rStyle = getRoleStyle(user.role?.name);
                    const subOk = isSubActive(user.subscription_end_date);
                    
                    return (
                      <tr 
                        key={user.id} 
                        className="au-row" 
                        style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(248, 250, 252, 0.5)' }}
                      >
                        {/* ID */}
                        <td style={{ padding: '12px 16px', fontSize: '11px', color: '#94a3b8', fontFamily: 'monospace' }}>
                          #{user.id}
                        </td>

                        {/* Name & Tenant */}
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {isSuspiciousAccount(user) && (
                              <span
                                title="Compte potentiellement suspect (sans nom complet, email alias ou domaine jetable)"
                                style={{ fontSize: '10px', background: '#fef9c3', color: '#92400e', borderRadius: '4px', padding: '1px 5px', fontWeight: 700, border: '1px solid #fbbf24', cursor: 'help', flexShrink: 0 }}
                              >
                                ⚠️ Suspect
                              </span>
                            )}
                            {user.full_name || 'Sans nom'}
                          </div>
                          {user.tenant && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: '#6366f1', fontWeight: 500 }}>
                              <span>🏢 {user.tenant.name}</span>
                              {user.tenant.plan && <span style={{ textTransform: 'uppercase', fontSize: '9px', background: '#e0e7ff', padding: '1px 4px', borderRadius: '4px' }}>{user.tenant.plan}</span>}
                            </div>
                          )}
                        </td>

                        {/* Email */}
                        <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                          <a href={`mailto:${user.email}`} style={{ color: '#2563eb', textDecoration: 'none' }}>
                            {user.email}
                          </a>
                        </td>

                        {/* Phone Badge */}
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          {user.phone_number ? (
                            <div style={{ display: 'inline-flex', alignItems: 'center', fontFamily: 'monospace', fontSize: '12px' }}>
                              {user.country_code && (
                                <span style={{ 
                                  background: '#e0f2fe', 
                                  color: '#0284c7', 
                                  padding: '1px 5px', 
                                  borderRadius: '4px', 
                                  fontSize: '10px', 
                                  fontWeight: 700, 
                                  marginRight: '6px' 
                                }}>
                                  +{user.country_code.replace(/^\+/, '')}
                                </span>
                              )}
                              <span>{user.phone_number}</span>
                            </div>
                          ) : (
                            <span style={{ color: '#cbd5e1' }}>—</span>
                          )}
                        </td>

                        {/* Role */}
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <span style={{
                            backgroundColor: rStyle.bg,
                            color: rStyle.color,
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 700,
                            whiteSpace: 'nowrap'
                          }}>
                            {user.role?.name || 'basic_user'}
                          </span>
                        </td>

                        {/* Status badges */}
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <div style={{ display: 'inline-flex', flexDirection: 'column', gap: '3px' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '20px',
                              fontSize: '10px',
                              fontWeight: 700,
                              backgroundColor: user.is_active ? '#ecfdf5' : '#fef2f2',
                              color: user.is_active ? '#059669' : '#dc2626',
                            }}>
                              {user.is_active ? 'Actif' : 'Inactif'}
                            </span>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '20px',
                              fontSize: '9px',
                              fontWeight: 700,
                              backgroundColor: user.is_email_verified ? '#f0f9ff' : '#fff7ed',
                              color: user.is_email_verified ? '#0284c7' : '#ea580c',
                            }}>
                              {user.is_email_verified ? 'Vérifié' : 'Non vérifié'}
                            </span>
                          </div>
                        </td>

                        {/* Subscription info */}
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          {user.subscription_end_date ? (
                            <div>
                              <span style={{
                                padding: '2px 6px',
                                borderRadius: '8px',
                                fontSize: '10px',
                                fontWeight: 700,
                                backgroundColor: subOk ? '#dcfce7' : '#fee2e2',
                                color: subOk ? '#166534' : '#991b1b',
                              }}>
                                {subOk ? '🔑 Actif' : '⏰ Expiré'}
                              </span>
                              <div style={{ fontSize: '10px', color: '#64748b', marginTop: '4px' }}>
                                {new Date(user.subscription_end_date).toLocaleDateString('fr-FR')}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#cbd5e1', fontSize: '12px' }}>—</span>
                          )}
                        </td>

                        {/* Last connection date + IP + Géoloc */}
                        <td style={{ padding: '12px 16px', textAlign: 'center', color: '#64748b', fontSize: '12px' }}>
                          {user.last_login ? (
                            <div>
                              <div style={{ fontWeight: 500 }}>{new Date(user.last_login).toLocaleDateString('fr-FR')}</div>
                              <div style={{ fontSize: '10px', color: '#94a3b8' }}>
                                {new Date(user.last_login).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              </div>
                              {((user as any).last_login_city || (user as any).last_login_country) && (
                                <div
                                  style={{ fontSize: '9px', color: '#6366f1', marginTop: '3px', fontWeight: 500 }}
                                  title={`IP: ${(user as any).last_login_ip || '?'} — ${(user as any).login_count || 0} connexion(s)`}
                                >
                                  📍 {[(user as any).last_login_city, (user as any).last_login_country].filter(Boolean).join(', ')}
                                </div>
                              )}
                              {!(user as any).last_login_city && (user as any).last_login_ip && (
                                <div style={{ fontSize: '9px', color: '#cbd5e1', marginTop: '2px', fontFamily: 'monospace' }} title={`${(user as any).login_count || 0} connexion(s)`}>
                                  {(user as any).last_login_ip}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: '#cbd5e1' }}>Jamais</span>
                          )}
                        </td>

                        {/* Inscription date */}
                        <td style={{ padding: '12px 16px', textAlign: 'center', color: '#64748b', fontSize: '12px' }}>
                          {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : 'Inconnue'}
                        </td>

                        {/* Action buttons */}
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                            <button
                              title='Modifier'
                              className="au-btn"
                              style={{
                                background: '#f1f5f9',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '6px 8px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                              onClick={() => setEditingUser(user)}
                            >
                              ✏️
                            </button>
                            <button
                              title={user.is_active ? 'Désactiver' : 'Activer'}
                              className="au-btn"
                              style={{
                                background: user.is_active ? '#fef2f2' : '#ecfdf5',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '6px 8px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                              onClick={() => handleToggleActive(user)}
                            >
                              {user.is_active ? '⛔' : '✅'}
                            </button>
                            <button
                              title="Renvoyer l'email de vérification"
                              className="au-btn"
                              style={{
                                background: '#e0f2fe',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '6px 8px',
                                cursor: 'pointer',
                                color: '#0369a1',
                                fontSize: '12px'
                              }}
                              onClick={() => handleResendVerification(user.email)}
                            >
                              ✉️
                            </button>
                            <button
                              title="Révoquer toutes les sessions (déconnecter)"
                              className="au-btn"
                              style={{
                                background: revokeLoadingId === user.id ? '#f3f4f6' : '#fdf4ff',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '6px 8px',
                                cursor: revokeLoadingId === user.id ? 'wait' : 'pointer',
                                color: '#7c3aed',
                                fontSize: '12px',
                              }}
                              onClick={() => handleRevokeSessions(user)}
                              disabled={revokeLoadingId === user.id}
                            >
                              🔐
                            </button>
                            {user.phone_number ? (
                              <a
                                href={getWhatsAppUrl(user.country_code, user.phone_number)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Envoyer un message WhatsApp"
                                className="au-btn"
                                style={{
                                  background: '#25d366',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '6px 8px',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  textDecoration: 'none'
                                }}
                              >
                                <WhatsAppIcon />
                              </a>
                            ) : (
                              <button
                                disabled
                                title="Pas de numéro de téléphone"
                                className="au-btn"
                                style={{
                                  background: '#e2e8f0',
                                  color: '#94a3b8',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '6px 8px',
                                  cursor: 'not-allowed',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <WhatsAppIcon />
                              </button>
                            )}
                          </div>
                        </td>

                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </Box>

        {/* Pagination Controls */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '20px',
          padding: '12px 20px',
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          {/* Page size selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#64748b' }}>
            <span>Afficher</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                ...inputSt,
                minWidth: 'auto',
                padding: '6px 10px',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>utilisateurs par page</span>
          </div>

          {/* Page indicator */}
          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
            Page {currentPage} sur {totalPages}
          </div>

          {/* Navigation buttons */}
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                background: currentPage === 1 ? '#f1f5f9' : 'white',
                color: currentPage === 1 ? '#cbd5e1' : '#475569',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                transition: 'all 0.2s'
              }}
            >
              Précédent
            </button>
            
            {/* Page number buttons */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              // Show pages centered around currentPage
              let pageNum = currentPage;
              if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              // Ensure pageNum is within bounds
              if (pageNum < 1 || pageNum > totalPages) return null;

              const isCurrent = pageNum === currentPage;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: isCurrent ? '1px solid #6366f1' : '1px solid #e2e8f0',
                    background: isCurrent ? '#6366f1' : 'white',
                    color: isCurrent ? 'white' : '#475569',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                background: currentPage === totalPages ? '#f1f5f9' : 'white',
                color: currentPage === totalPages ? '#cbd5e1' : '#475569',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                transition: 'all 0.2s'
              }}
            >
              Suivant
            </button>
          </div>
        </div>

        {/* Dialogs */}
        {editingUser && (
          <EditUserModal
            user={editingUser}
            roles={roles}
            tenants={tenants}
            onClose={() => setEditingUser(null)}
            onSave={handleSaveUser}
          />
        )}

        {showCreateModal && (
          <UserCreateModal
            roles={roles}
            tenants={tenants}
            onClose={() => setShowCreateModal(false)}
            onSave={handleCreateUser}
          />
        )}

        {/* Modal de confirmation générique — remplace tous les window.confirm() */}
        <ConfirmModal
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          isDestructive={confirmDialog.isDestructive}
          isLoading={confirmDialog.isLoading}
          confirmLabel="Confirmer"
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(d => ({ ...d, isOpen: false }))}
        />

      </div>
    </SiteLayout>
  );
};

export default UserAdministration;
